import React, { useCallback, useMemo, useState } from 'react'
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Switch
} from 'antd'
import { Guid } from 'admin/lib/guid'
import { useApi } from 'admin/lib/Api'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import produce from 'immer'

export function EditSmartWaste({ configData, isNew }) {
  const { accountId, projectId } = useParams()
  const [submitting, setSubmitting] = useState(false)

  const [form] = Form.useForm()

  const draftItem = useMemo(() => {
    return isNew ? blankConfig : configData
  }, [configData, isNew])

  const api = useApi()

  const onSubmit = useCallback(
    async data => {
      setSubmitting(true)

      const submissionData = produce(draftItem, draft => {
        draft.configurationId = draftItem.configurationId
        draft.publicKey = data.publicKey
        draft.privateKey = data.privateKey
        draft.clientId = data.clientId
        draft.userName = data.userName
        draft.isEnabled = data?.isEnabled
        draft.isDeleted = data?.isDeleted
        draft.projectPhaseId = data.projectPhaseId
        draft.projectId = projectId
        draft.smartWasteProjectConfigurations =
          data.smartWasteProjectConfigurations
        draft.defaultSWProjectId = data.defaultSWProjectId
        draft.isWasteEnabled = data.isWasteEnabled
        draft.isDeliveryEnabled = data.isDeliveryEnabled
      })

      const [, status, error] = isNew
        ? await api.registerSmartWasteConfig(
            accountId,
            projectId,
            submissionData
          )
        : await api.updateSmartWasteConfig(accountId, projectId, submissionData)

      if (error) {
        setSubmitting(false)
        Modal.error({
          title: 'Error SmartWaste configuration',
          content: (
            <>
              <p>Error, you may wish to try again. </p>
              <p>{`Details: ${status}: ${error}`}</p>
            </>
          )
        })
      } else {
        setSubmitting(false)
        notification.success({
          message: 'Successfully saved SmartWaste configuration'
        })
      }
    },
    [accountId, api, draftItem, isNew, projectId]
  )

  const layout = {
    labelCol: {
      span: 8
    },
    wrapperCol: {
      span: 16
    }
  }

  return (
    <Form
      {...layout}
      form={form}
      initialValues={draftItem}
      name="swConfig"
      onFinish={onSubmit}
    >
      <Form.Item
        name="userName"
        label="User Name"
        rules={[
          {
            minLength: 1,
            required: true
          }
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="clientId"
        label="Client Id"
        rules={[
          {
            minLength: 1,
            required: true
          }
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="publicKey"
        label="Public Key"
        rules={[
          {
            minLength: 1,
            required: true
          }
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="privateKey"
        label="Private Key"
        rules={[
          {
            minLength: 1,
            required: isNew
          }
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="projectPhaseId"
        rules={[{ minLength: 1, required: true }]}
        label="Project Phase"
      >
        <Select>
          <Select.Option value="1">Demolition</Select.Option>
          <Select.Option value="2">Excavation</Select.Option>
          <Select.Option value="3">Construction</Select.Option>
          <Select.Option value="5">Modular</Select.Option>
          <Select.Option value="6">Post-Completion</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item
        name="defaultSWProjectId"
        label="Default Project id"
        rules={[{ minLength: 1, required: true }]}
      >
        <Input />
      </Form.Item>
      <Row>
        <Col span={20}>
          <Form.Item
            name="isWasteEnabled"
            label="Waste Enabled"
            valuePropName="checked"
          >
            <Switch checked={form.isWasteEnabled} />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={20}>
          <Form.Item
            name="isDeliveryEnabled"
            label="Delivery Enabled"
            valuePropName="checked"
          >
            <Switch checked={form.isDeliveryEnabled} />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <Form.Item name="isEnabled" label="Enabled " valuePropName="checked">
            <Switch checked={form.isEnabled} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="isDeleted" label="Deleted " valuePropName="checked">
            <Switch checked={form.isDeleted} />
          </Form.Item>
        </Col>
      </Row>
      <Divider>Per-project config</Divider>
      <Form.List name="smartWasteProjectConfigurations">
        {(swpc, { add, remove }) => {
          return (
            <div>
              {swpc.map((field, index) => (
                <div key={field.key}>
                  <Divider>Entry {index + 1}</Divider>
                  <Form.Item
                    name={[index, 'emailAddress']}
                    label="Email Address"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="email address" type="email" />
                  </Form.Item>
                  <Form.Item
                    name={[index, 'swProjectId']}
                    label="SmartWaste Project Id"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="SmartWaste project Id" type="number" />
                  </Form.Item>
                  <Form.Item
                    name={[index, 'projectPhaseId']}
                    rules={[{ minLength: 1, required: true }]}
                    label="Project Phase"
                  >
                    <Select>
                      <Select.Option value="1">Demolition</Select.Option>
                      <Select.Option value="2">Excavation</Select.Option>
                      <Select.Option value="3">Construction</Select.Option>
                      <Select.Option value="5">Modular</Select.Option>
                      <Select.Option value="6">Post-Completion</Select.Option>
                    </Select>
                  </Form.Item>
                  {swpc.length > 0 ? (
                    <Button
                      type="danger"
                      className="dynamic-delete-button"
                      onClick={() => remove(field.name)}
                      icon={<MinusCircleOutlined />}
                    >
                      Remove previous entry
                    </Button>
                  ) : null}
                </div>
              ))}
              <Divider />
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  style={{ width: '60%' }}
                >
                  <PlusOutlined /> Add entry
                </Button>
              </Form.Item>
            </div>
          )
        }}
      </Form.List>
      <Form.Item>
        <Button type="primary" htmlType="submit" disabled={submitting}>
          {isNew ? 'Create' : 'Update'}
        </Button>
      </Form.Item>
    </Form>
  )
}

const blankConfig = {
  configurationId: Guid.Empty,
  publicKey: '',
  privateKey: '',
  clientId: '',
  userName: '',
  projectPhaseId: '',
  isEnabled: true,
  isDeleted: false,
  smartWasteProjectConfigurations: []
}
EditSmartWaste.propTypes = {
  configData: PropTypes.object,
  isNew: PropTypes.bool.isRequired
}
